import React from 'react';
import { ParticipantHubLayout } from './ParticipantHubLayout';
import { motion } from 'framer-motion';
import { TargetIcon, CalendarIcon, UsersIcon, PlusIcon, CheckCircle2Icon, CircleIcon, ArrowRightIcon, BookOpenIcon } from 'lucide-react';
export function ActionPlanPage() {
  return <ParticipantHubLayout data-id="element-5148">
      <div className="p-6 lg:p-8 max-w-5xl mx-auto space-y-8" data-id="element-5149">
        <div data-id="element-5150">
          <h1 className="font-serif-display text-4xl text-slate-900 mb-2" data-id="element-5151">
            My Action Plan
          </h1>
          <p className="text-slate-500" data-id="element-5152">
            Your commitments and integration strategy post-summit.
          </p>
        </div>

        {/* Hero Banner */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="bg-[#0B1B3B] rounded-2xl p-8 text-white relative overflow-hidden shadow-lg" data-id="element-5153">
          <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3" data-id="element-5154" />
          <div className="relative z-10" data-id="element-5155">
            <div className="flex items-center gap-2 text-amber-400 text-sm font-bold tracking-wider uppercase mb-4" data-id="element-5156">
              <TargetIcon className="w-4 h-4" data-id="element-5157" />
              90-Day Integration
            </div>
            <h2 className="font-serif-display text-3xl mb-4" data-id="element-5158">
              Your generated plan is taking shape.
            </h2>
            <p className="text-slate-300 max-w-2xl mb-6" data-id="element-5159">
              Based on your notes and session participation, we've drafted 3 key
              commitments. Review and finalize them before the closing session.
            </p>
            <div className="flex items-center gap-4" data-id="element-5160">
              <div className="flex -space-x-2" data-id="element-5161">
                <div className="w-8 h-8 rounded-full bg-slate-200 border-2 border-[#0B1B3B] z-20 overflow-hidden" data-id="element-5162">
                  <img src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=100&q=80" alt="Peer" className="w-full h-full object-cover" data-id="element-5163" />
                </div>
                <div className="w-8 h-8 rounded-full bg-slate-300 border-2 border-[#0B1B3B] z-10 overflow-hidden" data-id="element-5164">
                  <img src="https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&w=100&q=80" alt="Peer" className="w-full h-full object-cover" data-id="element-5165" />
                </div>
              </div>
              <span className="text-sm text-slate-300" data-id="element-5166">
                Shared with your accountability pod
              </span>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-5167">
          {/* Commitments List */}
          <div className="lg:col-span-2 space-y-6" data-id="element-5168">
            <div className="flex items-center justify-between" data-id="element-5169">
              <h3 className="font-serif-display text-2xl text-slate-900" data-id="element-5170">
                Key Commitments
              </h3>
              <button className="text-sm font-medium text-amber-600 flex items-center gap-1 hover:text-amber-700" data-id="element-5171">
                <PlusIcon className="w-4 h-4" data-id="element-5172" />
                Add Custom
              </button>
            </div>

            <div className="space-y-4" data-id="element-5173">
              {[{
              title: 'Implement AI Code Review Pilot',
              desc: 'Roll out the new AI review tool to the core platform team for a 30-day trial.',
              deadline: 'Nov 15, 2026',
              status: 'draft'
            }, {
              title: 'Cross-functional Sync with Product',
              desc: 'Establish bi-weekly alignment meetings with Elena to ensure roadmap synergy.',
              deadline: 'Oct 30, 2026',
              status: 'active'
            }, {
              title: 'Complete Leadership Assessment 360',
              desc: 'Finalize the post-retreat feedback loop with direct reports.',
              deadline: 'Nov 1, 2026',
              status: 'active'
            }].map((item, i) => <motion.div key={i} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.1 + i * 0.1
            }} className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm hover:shadow-md transition-shadow flex gap-4" data-id="element-5174">
                  <button className="mt-1 shrink-0" data-id="element-5175">
                    {item.status === 'done' ? <CheckCircle2Icon className="w-6 h-6 text-emerald-500" data-id="element-5176" /> : <CircleIcon className="w-6 h-6 text-slate-300 hover:text-amber-500 transition-colors" data-id="element-5177" />}
                  </button>
                  <div className="flex-1" data-id="element-5178">
                    <div className="flex items-start justify-between mb-1" data-id="element-5179">
                      <h4 className="font-bold text-slate-900" data-id="element-5180">{item.title}</h4>
                      {item.status === 'draft' && <span className="text-[10px] font-bold uppercase tracking-wider text-amber-600 bg-amber-50 px-2 py-0.5 rounded" data-id="element-5181">
                          Draft
                        </span>}
                    </div>
                    <p className="text-sm text-slate-600 mb-3" data-id="element-5182">{item.desc}</p>
                    <div className="flex items-center gap-4 text-xs font-medium text-slate-500" data-id="element-5183">
                      <div className="flex items-center gap-1" data-id="element-5184">
                        <CalendarIcon className="w-3.5 h-3.5" data-id="element-5185" />
                        Due: {item.deadline}
                      </div>
                      <button className="text-slate-400 hover:text-slate-600" data-id="element-5186">
                        Edit
                      </button>
                    </div>
                  </div>
                </motion.div>)}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6" data-id="element-5187">
            {/* Peer Check-in */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.4
          }} className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-id="element-5188">
              <div className="flex items-center gap-2 mb-4" data-id="element-5189">
                <UsersIcon className="w-5 h-5 text-amber-500" data-id="element-5190" />
                <h3 className="font-bold text-slate-900" data-id="element-5191">Peer Check-in</h3>
              </div>
              <p className="text-sm text-slate-600 mb-4" data-id="element-5192">
                You are paired with <strong data-id="element-5193">Marcus Chen</strong> for post-summit
                accountability.
              </p>
              <button className="w-full py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-700 hover:bg-slate-100 transition-colors" data-id="element-5194">
                Schedule 1:1 Sync
              </button>
            </motion.div>

            {/* Reflection Journal */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.5
          }} className="bg-amber-50 rounded-2xl border border-amber-100 p-6 shadow-sm" data-id="element-5195">
              <div className="flex items-center gap-2 mb-4" data-id="element-5196">
                <BookOpenIcon className="w-5 h-5 text-amber-600" data-id="element-5197" />
                <h3 className="font-bold text-slate-900" data-id="element-5198">Daily Reflection</h3>
              </div>
              <textarea placeholder="What was your biggest insight today?" className="w-full h-24 bg-white border border-amber-200 rounded-xl p-3 text-sm resize-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 outline-none mb-3 placeholder:text-slate-400" data-id="element-5199" />
              <button className="w-full py-2 bg-amber-600 text-white rounded-xl text-sm font-bold hover:bg-amber-700 transition-colors" data-id="element-5200">
                Save Entry
              </button>
            </motion.div>

            {/* Outcomes Preview */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.6
          }} className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-id="element-5201">
              <h3 className="font-bold text-slate-900 mb-2" data-id="element-5202">
                Organizational Impact
              </h3>
              <p className="text-xs text-slate-500 mb-4" data-id="element-5203">
                Your commitments roll up into the Finova Capital Outcomes
                Dashboard, helping measure the ROI of this summit.
              </p>
              <button className="text-sm text-amber-600 font-medium flex items-center gap-1 hover:text-amber-700" data-id="element-5204">
                View Team Dashboard <ArrowRightIcon className="w-4 h-4" data-id="element-5205" />
              </button>
            </motion.div>
          </div>
        </div>
      </div>
    </ParticipantHubLayout>;
}